import React, { FC, CSSProperties } from 'react'

import { Box } from 'components/Box'
import Markdown from 'components/Markdown'
import { stripMarkdownParser } from 'components/Markdown/parsers'
import Button from 'components/Button'
import TimeAgo from 'components/TimeAgo'
import Tooltip from 'components/Tooltip'
import { routes, versionedRoutes } from 'constants/routes'
import { formatByteCount } from 'utils'
import { NotificationInfoFragment } from 'gqlTypes'
// This is a valid use case to narrow down UserNotification type
// eslint-disable-next-line no-restricted-imports
import {
  CommentNotification,
  MentionNotification,
  AddShareMemberNotification,
  DownloadableAssetRenderedNotification,
} from 'gqlTypes/expansiveTypes'
import { useToast } from 'containers/ToastManager'
import { ErrorHandler } from 'modules/tracing'
import PersonAvatar from 'components/PersonAvatar'

import useAcceptShareMembershipInvitation from 'modules/shares/operations/useAcceptShareMembershipInvitation'

import {
  NotificationContainer,
  NotificationFlex,
  NotificationItemContainer,
  NotificationDescription,
  NotificationShareName,
  NotificationBody,
  NotificationsMeta,
  UnreadIndicator,
  FailedAsset,
  TooltipPopup,
  MarkAsReadButtonContainer,
  MarkAsReadIcon,
  FailedIcon,
  TooltipAvatar,
  DownloadAsset,
  AssetReadyIcon,
} from './NotificationListItem.styles'

/**
 * TYPES
 */
interface NotificationListItemProps {
  notification: NotificationInfoFragment
  markNotificationAsRead: () => void
  isVisuallyRead?: boolean
  isRead?: boolean
  onClick: (event: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => void
}

interface AcceptShareInviteButtonProps {
  shareIdentifier: string
  onCompleted: () => void
}

interface MarkAsReadButtonProps {
  onClick: (event: React.MouseEvent) => void
}

/**
 * HELPERS
 */
const pointerEventsNoneStyles: CSSProperties = { pointerEvents: 'none' }

const notificationPath = (notification: NotificationInfoFragment) => {
  if (isDownloadableAssetNotification(notification)) {
    if (!notification?.share || !notification?.notifiedVersion) {
      ErrorHandler.shouldNeverHappen('Invalid share or version Id')
      // At this point, return an emtpy string or router link will fail
      // the validation
      return ''
    }

    return versionedRoutes.SHARE_VIEW.VERSION.create({
      shareID: notification?.share?.identifier,
      versionShortId: notification?.notifiedVersion?.shortId,
    })
  }

  if (!notification.path) {
    if (
      isCommentNotification(notification) &&
      notification.share &&
      notification.artboard?.permanentArtboardShortId
    ) {
      return routes.ARTBOARD_DETAIL.create({
        shareID: notification.share.identifier,
        permanentArtboardShortId:
          notification.artboard.permanentArtboardShortId,
      })
    }

    ErrorHandler.shouldNeverHappen('Invalid path')
    // At this point, return an emtpy string or router link will fail
    // the validation
    return ''
  }

  return notification.path
}

const isDownloadableAssetReady = (notification: NotificationInfoFragment) =>
  isDownloadableAssetNotification(notification) &&
  notification?.notifiedVersion?.document?.downloadableAssets?.[0]?.status ===
    'AVAILABLE'

const isDownloadableAssetFailed = (notification: NotificationInfoFragment) =>
  isDownloadableAssetNotification(notification) &&
  notification?.notifiedVersion?.document?.downloadableAssets?.[0]?.status ===
    'FAILED'

const getDownloadableAsset = (notification: NotificationInfoFragment) => {
  if (
    !isDownloadableAssetReady(notification) ||
    !isDownloadableAssetNotification(notification)
  ) {
    return undefined
  }

  return notification?.notifiedVersion?.document?.downloadableAssets[0]
}

// User defined type guards to easily narrow down NotificationInfoFragment
const isCommentNotification = (
  notification?: NotificationInfoFragment
): notification is CommentNotification =>
  notification?.__typename === 'CommentNotification'

const isMentionNotification = (
  notification?: NotificationInfoFragment
): notification is MentionNotification =>
  notification?.__typename === 'MentionNotification'

const isAddShareMemberNotification = (
  notification?: NotificationInfoFragment
): notification is AddShareMemberNotification =>
  notification?.__typename === 'AddShareMemberNotification'

const isDownloadableAssetNotification = (
  notification?: NotificationInfoFragment
): notification is DownloadableAssetRenderedNotification =>
  notification?.__typename === 'DownloadableAssetRenderedNotification'

/**
 * HELPER COMPONENTS
 */
const MarkAsReadButton: FC<MarkAsReadButtonProps> = ({ onClick, ...props }) => {
  return (
    <Tooltip
      spacing="12px"
      content={<TooltipPopup>Mark as read</TooltipPopup>}
      // HACK: set pointer events to none to prevent hover over the tooltip
      // itself which would make the tooltip dissapear for a short while and
      // then reappear again resulting in flickering
      contentStyle={pointerEventsNoneStyles}
      {...props}>
      <Button aria-label="Mark notification as read" onClick={onClick} small>
        <MarkAsReadIcon />
      </Button>
    </Tooltip>
  )
}

const AcceptShareInviteButton: FC<AcceptShareInviteButtonProps> = ({
  shareIdentifier,
  onCompleted,
}) => {
  const { showToast } = useToast()
  const [acceptMembership, { loading }] = useAcceptShareMembershipInvitation({
    variables: { shareIdentifier },
    onError: 'show-toast',
    onCompleted: ({ acceptShareMembershipInvitation }) => {
      if (!acceptShareMembershipInvitation) {
        ErrorHandler.shouldNeverHappen.invalidMutationData(
          'acceptShareMembershipInvitation'
        )
        return
      }

      showToast('Invite successfully accepted')

      onCompleted()
    },
  })

  return (
    <Button
      variant="primary"
      mt={4}
      onClick={event => {
        /* Prevent the link click action */
        event.preventDefault()
        /* Prevent the dropdown from closing */
        event.stopPropagation()

        acceptMembership()
      }}
      loading={loading}
      tiny>
      Accept Invite
    </Button>
  )
}

const NotificationAvatar: React.FC<{
  avatar: string
  name: string
}> = ({ avatar, name }) => (
  <Box UNSAFE_paddingTop="1px" marginRight={2}>
    {avatar ? (
      <TooltipAvatar content={name}>
        <PersonAvatar flavour="image" size="16px" name={name} src={avatar} />
      </TooltipAvatar>
    ) : (
      <PersonAvatar flavour="color" size="16px" name={name} />
    )}
  </Box>
)

/**
 * MAIN COMPONENT
 */
const NotificationListItem: FC<NotificationListItemProps> = ({
  notification,
  markNotificationAsRead,
  isVisuallyRead,
  isRead,
  ...props
}) => {
  const notificationDescription = notification.subtitle!
  const userAvatar = notification.user?.avatar?.small

  const handleDownloadAsset = (e: React.SyntheticEvent) => {
    // Prevent closing the dropdown
    e.stopPropagation()

    // Prevent the link click action
    e.preventDefault()

    const downloadableAssetPath = getDownloadableAsset(notification)?.path
    if (downloadableAssetPath) {
      window.open(downloadableAssetPath, '_self')
      // added here since we're preventing the normal NotificationListItem onClick above
      markNotificationAsRead()
    }
  }

  // Renders different type of notifications
  const renderNotification = () => {
    // COMMENT NOTIFICATION
    if (isCommentNotification(notification)) {
      return (
        <>
          <NotificationShareName data-testid="notification-share-name">
            {notification?.share?.workspace.name} / {notification?.share?.name}{' '}
            / {notification?.artboard?.name}
          </NotificationShareName>
          <NotificationBody data-testid="notification-body">
            <NotificationAvatar
              avatar={userAvatar ?? ''}
              name={notification.user?.name ?? ''}
            />
            <Markdown parser={stripMarkdownParser}>
              {notification?.comment?.body}
            </Markdown>
          </NotificationBody>
        </>
      )
    }

    // MENTION NOTIFICATION
    if (isMentionNotification(notification)) {
      // BE is returning `FORBIDDEN` code and `USER_CANT_VIEW_SHARE` reason
      // when the user doesn't have access to comments on Artobards (when the
      // user really doesn't have access to a share the notification is not
      // returned from BE even if the user was mentioned)
      const shareName = notification?.share
        ? `${notification?.share?.workspace.name} / ${notification?.share?.name} / ${notification?.artboard?.name}`
        : notification.title

      const body = notification?.comment
        ? notification?.comment?.body
        : `${notification?.user?.name} mentioned you`

      return (
        <>
          <NotificationShareName data-testid="notification-share-name">
            {shareName}
          </NotificationShareName>
          <NotificationBody data-testid="notification-body">
            <NotificationAvatar
              avatar={userAvatar ?? ''}
              name={notification.user?.name ?? ''}
            />
            <Markdown parser={stripMarkdownParser}>{body}</Markdown>
          </NotificationBody>
        </>
      )
    }

    // MEMBERSHIP NOTIFICATION
    if (isAddShareMemberNotification(notification)) {
      return (
        <>
          <NotificationShareName data-testid="notification-share-name">
            {notification?.share?.workspace.name} / {notification?.share?.name}{' '}
            / {notification?.share?.artboard?.name}
          </NotificationShareName>
          <NotificationBody data-testid="notification-body">
            <NotificationAvatar
              avatar={userAvatar ?? ''}
              name={notification.user?.name ?? ''}
            />
            <NotificationDescription data-testid="notification-description">
              {notificationDescription}
            </NotificationDescription>
          </NotificationBody>

          {notification.share?.userHasUnacceptedInvitationToView && (
            <Box marginTop={2}>
              <AcceptShareInviteButton
                shareIdentifier={notification.share.identifier}
                onCompleted={markNotificationAsRead}
              />
            </Box>
          )}
        </>
      )
    }

    // ASSET READY NOTIFICATION
    // `isDownloadableAssetReady` already uses `isDownloadableAssetNotification`
    // but we need it also here to force TS to use the correct type
    if (
      isDownloadableAssetNotification(notification) &&
      isDownloadableAssetReady(notification)
    ) {
      return (
        <>
          <NotificationShareName data-testid="notification-share-name">
            {notification?.share?.workspace.name} / {notification?.share?.name}
          </NotificationShareName>
          <NotificationBody data-testid="notification-body">
            <AssetReadyIcon />
            <div>
              Your assets are ready to{' '}
              <DownloadAsset
                onClick={handleDownloadAsset}
                data-testid="download-assets-button">
                download (ZIP{' '}
                {formatByteCount(
                  getDownloadableAsset(notification)?.fileSizeInBytes!
                )}
                )
              </DownloadAsset>
            </div>
          </NotificationBody>
        </>
      )
    }

    // ASSET FAILED NOTIFICATION
    // `isDownloadableAssetFailed` already uses `isDownloadableAssetNotification`
    // but we need it also here to force TS to use the correct type
    if (
      isDownloadableAssetNotification(notification) &&
      isDownloadableAssetFailed(notification)
    ) {
      return (
        <>
          <NotificationShareName data-testid="notification-share-name">
            {notification?.share?.workspace.name} / {notification?.share?.name}
          </NotificationShareName>
          <NotificationDescription data-testid="notification-description">
            <FailedAsset>
              <FailedIcon /> Failed to export assets
            </FailedAsset>
          </NotificationDescription>
          <NotificationBody data-testid="notification-body">
            Please try again, if this error persists, try uploading your
            document again.
          </NotificationBody>
        </>
      )
    }

    // ANY OTHER NOTIFICATION
    // We should never reach this point as every type of notification should
    // be handled individually
    ErrorHandler.shouldNeverHappen(
      `This type of notification is not recognized: ${notification.__typename}`
    )
  }

  /**
   * Standard notification structure.
   *
   * We have 5 types of notifications with slightly different structure:
   * - Comment/Reply
   * - Mention
   * - Membership
   * - Assets ready
   * - Assets failed
   *
   * UI wise we show the `read` as a dot positioned to the left of the
   * notification, and we split the `read` and `unread` notifications by
   * splitting the list.
   */
  return (
    <NotificationContainer
      data-testid="notification-list-item"
      to={notificationPath(notification)}
      $isRead={isRead}
      {...props}>
      {!isVisuallyRead && (
        <UnreadIndicator
          aria-label="Unread notification"
          data-testid="notification-unread-indicator"
        />
      )}

      <NotificationFlex>
        <NotificationItemContainer>
          {renderNotification()}
        </NotificationItemContainer>
        <NotificationsMeta>
          <TimeAgo date={notification.date} />
        </NotificationsMeta>

        <MarkAsReadButtonContainer>
          {!isVisuallyRead && (
            <MarkAsReadButton
              onClick={event => {
                // Prevent click from closing the popover
                event.preventDefault()
                event.stopPropagation()
                setTimeout(() => {
                  markNotificationAsRead()
                })
              }}
            />
          )}
        </MarkAsReadButtonContainer>
      </NotificationFlex>
    </NotificationContainer>
  )
}

export default NotificationListItem
